import { IntegrationScope, IntegrationToken } from '../../../../types/responses/Interoperability';

// Paginação é a mesma das outras listagens que trazem o conjunto completo.
export { clampPage, countLabel, pageOf } from '../listPaging';

/**
 * Filtro da lista de tokens de integração.
 *
 * Aplicado no cliente, e isso é correto e não atalho: o endpoint devolve **todos** os tokens
 * da conta, sem paginação de servidor. Com o conjunto completo em mãos, filtrar e paginar
 * aqui dá o mesmo resultado que faria no servidor.
 *
 * Se algum dia a listagem passar a ser paginada no servidor, este módulo tem de ir com ela —
 * filtrar uma página e chamar isso de filtro é o engano clássico.
 */

export type IntegrationTokenState = 'ALL' | 'ACTIVE' | 'EXPIRED' | 'REVOKED';

export type IntegrationTokenFilter = {
  /** Busca por integrador ou representante. */
  search: string;

  state: IntegrationTokenState;

  /** Vazio significa qualquer escopo. */
  scope: IntegrationScope | '';
};

export const EMPTY_INTEGRATION_FILTER: IntegrationTokenFilter = {
  search: '',
  state: 'ALL',
  scope: '',
};

/** O estado do token, na mesma classificação que a tela mostra. */
export const stateOf = (token: IntegrationToken): Exclude<IntegrationTokenState, 'ALL'> => {
  if (token.revoked_at) {
    return 'REVOKED';
  }

  // Não revogado e inativo só pode ser vencimento.
  return token.active ? 'ACTIVE' : 'EXPIRED';
};

const matchesSearch = (token: IntegrationToken, search: string): boolean => {
  const term = search.trim().toLowerCase();

  if (!term) {
    return true;
  }

  // Nome e representante: são os dois jeitos de a pessoa lembrar de um token. O hint entra
  // também, para quem tem o valor em mãos e quer achar a linha.
  return [token.name, token.contact_email, token.token_hint]
    .filter((field): field is string => !!field)
    .some((field) => field.toLowerCase().includes(term));
};

export const filterIntegrationTokens = (
  tokens: IntegrationToken[],
  filter: IntegrationTokenFilter,
): IntegrationToken[] =>
  tokens.filter((token) => {
    if (!matchesSearch(token, filter.search)) {
      return false;
    }

    if (filter.state !== 'ALL' && stateOf(token) !== filter.state) {
      return false;
    }

    if (filter.scope && !token.scopes.includes(filter.scope)) {
      return false;
    }

    return true;
  });
